// src/components/preferences/JobPreferenceEditor.tsx
/**
 * Editor for job preferences (job family, roles, seniority, search status)
 * ⚠️ FIXED: Uses correct field names matching backend schema
 */
import React from 'react';
import {
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonList,
  IonChip,
  IonIcon,
  IonButton,
} from '@ionic/react';
import { closeCircle } from 'ionicons/icons';
import { JobPreferences, SENIORITY_LEVELS, SEARCH_STATUSES } from '../../types/preferences.ts';
import './PreferenceEditor.css';

interface JobPreferenceEditorProps {
  data: JobPreferences;
  onChange: (data: JobPreferences) => void;
}

const JobPreferenceEditor: React.FC<JobPreferenceEditorProps> = ({ data, onChange }) => {
  const [newRole, setNewRole] = React.useState('');

  const handleFieldChange = (field: keyof JobPreferences, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleAddRole = () => {
    if (newRole.trim() && data.roles) {
      onChange({
        ...data,
        roles: [...data.roles, newRole.trim()],
      });
      setNewRole('');
    } else if (newRole.trim()) {
      onChange({
        ...data,
        roles: [newRole.trim()],
      });
      setNewRole('');
    }
  };

  const handleRemoveRole = (index: number) => {
    if (data.roles) {
      onChange({
        ...data,
        roles: data.roles.filter((_, i) => i !== index),
      });
    }
  };

  return (
    <IonList className="preference-editor-list">
      <IonItem>
        <IonLabel position="stacked">Job Family</IonLabel>
        <IonInput
          value={data.family || ''}
          placeholder="e.g., Engineering (Software), Marketing, Sales"
          onIonChange={(e) => handleFieldChange('family', e.detail.value)}
        />
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">Job Roles</IonLabel>
        <div className="chip-input-container">
          {data.roles && data.roles.length > 0 && (
            <div className="chip-list">
              {data.roles.map((role, index) => (
                <IonChip key={index} color="primary">
                  {role}
                  <IonIcon
                    icon={closeCircle}
                    onClick={() => handleRemoveRole(index)}
                  />
                </IonChip>
              ))}
            </div>
          )}
          <div className="chip-input">
            <IonInput
              value={newRole}
              placeholder="Add job role (e.g., Backend Engineer)"
              onIonChange={(e) => setNewRole(e.detail.value || '')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddRole();
                }
              }}
            />
            <IonButton onClick={handleAddRole} size="small">
              Add
            </IonButton>
          </div>
        </div>
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">Seniority Level</IonLabel>
        <IonSelect
          value={data.seniority || ''}
          placeholder="Select seniority level"
          onIonChange={(e) => handleFieldChange('seniority', e.detail.value)}
        >
          {SENIORITY_LEVELS.map((level) => (
            <IonSelectOption key={level} value={level}>
              {level.charAt(0).toUpperCase() + level.slice(1)}
            </IonSelectOption>
          ))}
        </IonSelect>
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">Search Status</IonLabel>
        <IonSelect
          value={data.search_status || ''}
          placeholder="Select search status"
          onIonChange={(e) => handleFieldChange('search_status', e.detail.value)}
        >
          {SEARCH_STATUSES.map((status) => (
            <IonSelectOption key={status.value} value={status.value}>
              {status.label}
            </IonSelectOption>
          ))}
        </IonSelect>
      </IonItem>
    </IonList>
  );
};

export default JobPreferenceEditor;
